<template>
  <div class="condensed-pricing-summary content--nobreak">
    <h1>Pricing Summary</h1>
    <h2>{{ quoteType }}</h2>
    <div class="material-info columns">
      <table class="table has-tiny-font">
        <thead>
          <tr>
            <th colspan="2"><div class="orange-dot">Material</div></th>
            <th><div class="orange-dot">Sample Image</div></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Architectural Name</td>
            <td>{{ item.architecture_name }}</td>
            <td rowspan="3">
              <div class="img-wrapper">
                <img 
                  :src="material.product_master.image" 
                  :alt="material.product_master.name"
                />
              </div>
            </td>
          </tr>
          <tr>
            <td>Material Name</td>
            <td>{{ material.product_master.name }}</td>
          </tr>
          <tr>
            <td colspan="2"><div class="empty">&nbsp;</div></td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <table class="table has-tiny-font pricing-summary mt-5">
      <thead>
        <tr class="has-text-centered">
          <th v-for="(header, i) in headers" :key="i">
            <div v-html="header.label" class="orange-dot"></div>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="elevation in item.elevations" :key="elevation.id">
          <td v-for="(col, colIndex) in headers" :key="colIndex">
            {{ col.render(elevation) }}
          </td>
        </tr>
      </tbody>

      <tfoot>
        <tr class="v-centered has-text-right has-text-weight-bold">
          <td>
            Total
          </td>
          <td 
            v-for="(col, colIndex) in headers.filter(h => h.label !== 'Elevation')" 
            :key="colIndex"
            :class="col.totalClass"
          >
            {{ col.total }}
          </td>
        </tr>
        <tr class="v-centered has-text-right has-text-weight-bold">
          <td 
            :colspan="headers.length === 9 
              ? headers.length - 3 
              : headers.length -2"
          >
          </td>
          <td :colspan="headers.length === 9 ? 2 : 1" class="text--nowrap">
            Item Total
          </td>
          <td>{{ grandTotal | currency }}</td>
        </tr>
      </tfoot>
    </table>

    <div class="summary-info">
      <div v-if="budgetSummary" class="columns pricing-desc my-0">
        <div class="column is-4">
          <div class="summary-info__label orange-dot">Budget Summary</div>
        </div>
        <div class="column is-8">
          <v-rich-text-renderer 
            :value="budgetSummary.value" 
            :replacements="replacements" 
          />
        </div>
      </div>
      <div v-if="notes" class="columns pricing-desc mt-3">
        <div class="column is-4">
          <div class="summary-info__label orange-dot">Notes</div>
        </div>
        <div class="column is-8">
          <div class="notes-content"><span v-html="notes.value"></span></div>
        </div>
      </div>
      <div v-if="genericTimeline" class="mt-5">
        <h2 class="standalone">Generic Estimated Timeline</h2>
        <div>
          <img v-bind="genericTimeline.display.bind" class="mt-3" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VRichTextRenderer from '@/components/base/VRichTextRenderer'
import { rfqQuoteTypes } from '@/data/rfq-quote-types'
import { condensedEstimationHelpers } from '@/mixins/helpers'

export default {
  props: {
    rfq: {
      type: Object,
      required: true
    },
    estimation: {
      type: Object,
      required: true
    },
    info: {
      type: Array,
      required: true
    },
    proposal: {
      type: Object,
      require: true
    }
  },
  components: { VRichTextRenderer },
  mixins: [condensedEstimationHelpers],
  computed: {
    quoteType({ rfq }) {
      return rfqQuoteTypes.find(t => t.value === rfq.quote_type).display
    },
    estimationItemId({ info }) {
      return info.find(i => i.id === 'content').display.bind.estimationItemId
    },
    item({ estimation, estimationItemId }) {
      return estimation.items.find(item => item.id === estimationItemId)
    },
    material({ item }) {
      return item.materials.find(m => [13, 20].includes(m.product_master.product_category_id))
    },
    isRailing({ material }) {
      return material.product_master.product_category_id === 20
    },
    headers({ material, item, isRailing, itemTotalReturns }) {
      if (material.product_master.product_brand === 'Mitrex') {
        if (isRailing) {
          return [
            { label: 'Elevation', render: e => e.elevation.elevation_name },
            { 
              label: 'Active <span class="is-tiny has-text-weight-normal">(LF)</span>', 
              render: (e) => this.$options.filters.commasep(e.manual_active_sqft),
              total: this.$options.filters.commasep(
                item.elevations.map(e => parseFloat(e.manual_active_sqft)).reduce((a,b) => a + b, 0)
              )
            },
            { 
              label: '$ <span class="is-tiny has-text-weight-normal">per elevation</span>', 
              render: (e) => this.$options.filters.currency(e.manual_active_sqft * material.manual_active_dollar_sqft),
              total: this.$options.filters.currency(
                item.elevations
                  .map(e => e.manual_active_sqft * material.manual_active_dollar_sqft)
                  .reduce((a,b) => a + b, 0)
              )
            },
            { 
              label: '~KW', 
              render: (e) => this.$options.filters.commasep(this.getKw(e)),
              total: this.$options.filters.commasep(
                item.elevations.map(e => this.getKw(e)).reduce((a,b) => a + b, 0), 
                0
              ),
              totalClass: 'env-benefit'
            },
            { 
              label: 'BIPV <span class="is-tiny has-text-weight-normal">~Kwh/Year</span>',
              render: (e) => this.$options.filters.commasep(this.getKwhPerYear(e)),
              total: this.$options.filters.commasep(
                item.elevations.map(e => this.getKwhPerYear(e)).reduce((a,b) => a + b, 0), 
                0
              ),
              totalClass: 'env-benefit'
            }
          ]
        } else {
          return [
            { label: 'Elevation', render: e => e.elevation.elevation_name },
            { 
              label: 'Active <span class="is-tiny has-text-weight-normal">(SQFT)</span>', 
              render: (e) => this.$options.filters.commasep(e.manual_active_sqft),
              total: this.$options.filters.commasep(
                item.elevations.map(e => parseFloat(e.manual_active_sqft)).reduce((a,b) => a + b, 0)
              )
            },
            { 
              label: '$ <span class="is-tiny has-text-weight-normal">per elevation</span>', 
              render: (e) => this.$options.filters.currency(e.manual_active_sqft * material.manual_active_dollar_sqft),
              total: this.$options.filters.currency(
                item.elevations
                  .map(e => e.manual_active_sqft * material.manual_active_dollar_sqft)
                  .reduce((a,b) => a + b, 0)
              )
            },
            { 
              label: 'Inactive <span class="is-tiny has-text-weight-normal">(SQFT)</span>',
              render: (e) => this.$options.filters.commasep(e.manual_non_active_sqft),
              total: this.$options.filters.commasep(
                item.elevations.map(e => parseFloat(e.manual_non_active_sqft)).reduce((a,b) => a + b, 0)
              )
            },
            { 
              label: '$ <span class="is-tiny has-text-weight-normal">per elevation</span>',
              render: (e) => this.$options.filters.currency(e.manual_non_active_sqft * material.manual_non_active_dollar_sqft),
              total: this.$options.filters.currency(
                item.elevations
                  .map(e => e.manual_non_active_sqft * material.manual_non_active_dollar_sqft)
                  .reduce((a,b) => a + b, 0)
              )
            },
            itemTotalReturns > 0 
              ? { 
                  label: 'Returns & Flashings <span class="is-tiny has-text-weight-normal">(LFT)</span>',
                  render: (e) => this.$options.filters.commasep(e.manual_returns_and_flashings_sqft),
                  total: this.$options.filters.commasep(
                    item.elevations.map(e => parseFloat(e.manual_returns_and_flashings_sqft)).reduce((a,b) => a + b, 0)
                  )
                }
              : null
            ,
            itemTotalReturns > 0 
              ? { 
                  label: '$ <span class="is-tiny has-text-weight-normal">per elevation</span>',
                  render: (e) => this.$options.filters.currency(e.manual_returns_and_flashings_sqft * material.manual_returns_dollar_sqft),
                  total: this.$options.filters.currency(
                    item.elevations
                      .map(e => e.manual_returns_and_flashings_sqft * material.manual_returns_dollar_sqft)
                      .reduce((a,b) => a + b, 0)
                  )
                }
              : null
            ,
            { 
              label: '~KW', 
              render: (e) => this.$options.filters.commasep(this.getKw(e)),
              total: this.$options.filters.commasep(
                item.elevations.map(e => this.getKw(e)).reduce((a,b) => a + b, 0), 
                0
              ),
              totalClass: 'env-benefit'
            },
            { 
              label: 'BIPV <span class="is-tiny has-text-weight-normal">~Kwh/Year</span>',
              render: (e) => this.$options.filters.commasep(this.getKwhPerYear(e)),
              total: this.$options.filters.commasep(
                item.elevations.map(e => this.getKwhPerYear(e)).reduce((a,b) => a + b, 0), 
                0
              ),
              totalClass: 'env-benefit'
            }
          ].filter(col => !!col)
        }
      } else {
        return [
          { label: 'Elevation', render: e => e.elevation.elevation_name },
          { 
            label: 'Area <span class="is-tiny has-text-weight-normal">(SQFT)</span>',
            render: (e) => this.$options.filters.commasep(e.manual_non_active_sqft),
            total: this.$options.filters.commasep(
              item.elevations.map(e => parseFloat(e.manual_non_active_sqft)).reduce((a,b) => a + b, 0)
            )
          },
          { 
            label: '$ <span class="is-tiny has-text-weight-normal">per elevation</span>',
            render: (e) => this.$options.filters.currency(e.manual_non_active_sqft * material.manual_non_active_dollar_sqft),
            total: this.$options.filters.currency(
              item.elevations
                .map(e => e.manual_non_active_sqft * material.manual_non_active_dollar_sqft)
                .reduce((a,b) => a + b, 0)
            )
          },
          itemTotalReturns > 0 
            ? { 
                label: 'Returns & Flashings <span class="is-tiny has-text-weight-normal">(LF)</span>',
                render: (e) => this.$options.filters.commasep(e.manual_returns_and_flashings_sqft),
                total: this.$options.filters.commasep(
                  item.elevations.map(e => parseFloat(e.manual_returns_and_flashings_sqft)).reduce((a,b) => a + b, 0)
                )
              } 
            : null
          ,
          itemTotalReturns > 0 
            ? { 
                label: '$ <span class="is-tiny has-text-weight-normal">per elevation</span>',
                render: (e) => this.$options.filters.currency(e.manual_returns_and_flashings_sqft * material.manual_returns_dollar_sqft),
                total: this.$options.filters.currency(
                  item.elevations
                    .map(e => e.manual_returns_and_flashings_sqft * material.manual_returns_dollar_sqft)
                    .reduce((a,b) => a + b, 0)
                )
              }
            : null
        ].filter(col => !!col)
      }
    },
    notes({ info }) {
      return info.find(i => i.id === 'notes')
    },
    genericTimeline({ info }) {
      return info.find(i => i.id === 'generic_estimated_timeline')
    },
    budgetSummary({ info }) {
      return info.find(i => i.id === 'budget_summary')
    },
    estimationElevations({ estimation }) {
      return estimation.items.map(item => item.elevations).flat(1)
    },
    summaryTotalPanel({ estimationElevations }) {
      return estimationElevations
        .filter(e => e.wall_railing === 'wall')
        .reduce((a,b) => {
          let res = a;
          res += parseFloat(b.manual_active_sqft)
          res += parseFloat(b.manual_non_active_sqft)
          return res
        }, 0)
    },
    summaryTotalRail({ estimationElevations }) {
      return estimationElevations
        .filter(e => e.wall_railing === 'rail')
        .reduce((a,b) => {
          let res = a;
          res += parseFloat(b.manual_active_sqft)
          res += parseFloat(b.manual_non_active_sqft)
          return res
        }, 0)
    },
    summaryTotalReturns({ estimationElevations }) {
      return estimationElevations
        .map(e => parseFloat(e.manual_returns_and_flashings_sqft))
        .reduce((a,b) => a + b, 0)
    },
    itemTotalReturns({ item }) {
      return item.elevations
        .map(e => parseFloat(e.manual_returns_and_flashings_sqft))
        .reduce((a,b) => a + b, 0)
    },
    summaryTotalDollars({ estimation, estimationElevations }) {
      return estimationElevations.reduce((a,b) => {
        let res = a;
        const item = estimation.items.find(item => item.id === b.estimation_item_id)
        const material = item.materials.find(m => [13, 20].includes(m.product_master.product_category_id))
        res += b.manual_non_active_sqft * material.manual_non_active_dollar_sqft
        res += b.manual_returns_and_flashings_sqft * material.manual_returns_dollar_sqft
        if (material.product_master.product_brand === 'Mitrex') {
          res += b.manual_active_sqft * material.manual_active_dollar_sqft
        }
        return res
      }, 0)
    },
    replacements({ 
      summaryTotalPanel, 
      summaryTotalRail, 
      summaryTotalReturns, 
      summaryTotalDollars, 
      rfq, 
      proposal
    }) {
      const res = [];
      const supply = rfq.has_supply ? 'supply' : '';
      const install = rfq.has_install ? 'install' : '';
      const supplyInstall = supply && install 
        ? `${supply} and ${install}`
        : supply || install
      let installMethods = proposal.proposal_content
        .find(section => section.id === 'install_method').subsections
        .map(subsection => subsection.method)
      if (installMethods.length > 1) {
        installMethods = installMethods.map((method, i) => {
          if (i + 2 === installMethods.length) {
            return `${method}${installMethods.length > 2 ? ',' : ''} and `
          } else if (i + 1 < installMethods.length) {
            return `${method}, `
          } else {
            return method
          }
        })
      }
      res.push([
        '{SUPPLY_INSTALL}', 
        `<span class="has-text-weight-semibold">${supplyInstall}</span>`
      ]);

      const areaText = `<span class="has-text-weight-semibold"> ${this.$options.filters.commasep(summaryTotalPanel, 0)} SQFT</span> of metal panel`
      const railingText = `<span class="has-text-weight-semibold"> ${this.$options.filters.commasep(summaryTotalRail, 0)} SQFT</span> of railing`
      const returnsText = `<span class="has-text-weight-semibold"> ${this.$options.filters.commasep(summaryTotalReturns, 0)} LF</span> of returns and flashings`
      let sqftPerType = []
      if (summaryTotalPanel > 0) {
        sqftPerType.push(areaText)
      }
      if (summaryTotalRail > 0) {
        sqftPerType.push(railingText)
      }
      if (summaryTotalReturns > 0) {
        sqftPerType.push(returnsText)
      }
      sqftPerType = sqftPerType.map((type, i) => {
        if (i + 2 === sqftPerType.length) {
          return `${type}${sqftPerType.length > 2 ? ',' : ''} and `
        } else if (i + 1 < sqftPerType.length) {
          return `${type}, `
        } else {
          return type
        }
      })

      res.push([
        '{TOTAL_SQFT_PER_TYPE}', 
        sqftPerType.join(' ')
      ]);
      res.push([
        '{CONDENSED_INSTALLATION_METHODS}', 
        `<span class="has-text-weight-semibold">${installMethods.join('')}</span>`
      ]);
      res.push([
        '{TOTAL_PRICE}', 
        `<span class="has-text-weight-semibold">${this.$options.filters.currency(summaryTotalDollars)}</span>`
      ]);
      return res
    }
  },
  methods: {
    getKw(elevation) {
      return elevation.manual_active_sqft * this.material.manual_avg_kw_sqft / 1000
    },
    getKwhPerYear(elevation) {
      const elevationName = elevation.elevation.elevation_name
      return elevationName 
        ? this.estimation.manual_sun_hours_obj[elevationName.toLowerCase()] * this.getKw(elevation)
        : 0
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/flexbox.scss';
@import '@/styles/table.scss';

.material-info {
  .table {
    width: 100%;
  }
  thead th {
    color: #181A19;
    border-top: 1px solid #D5D3CE;
    border-bottom: 3px solid white;
    font-size: 0.8rem !important;
    &:not(:last-child) {
      border-right: 1px solid #D5D3CE;
    }
  }
  td {
    border-bottom: none;
  }
  tr:first-child {
    td:not(:last-child) {
      border-bottom: 1px solid #D5D3CE;
    }
  }
  .img-wrapper {
    img {
      height: 80px;
      width: auto;
      margin: 0px auto;
    }
  }

  .empty {
    height: 15px;
  }
}

.pricing-summary {
  width: 100%;
  font-weight: lighter;
  thead {
    font-size: 1.05rem;
    color: #181A19;
    th {
      border-top: 1px solid #D5D3CE;
      border-bottom: 3px solid white;
      &:not(:last-child) {
        border-right: 1px solid #D5D3CE;
      }
    }
  }
  tbody td {
    border-bottom: 1px solid #D5D3CE;
  }
  tbody tr {
    color: #181A19;
    td:first-child {
      color: #181A19;
      font-weight: bold;
    }
    td:not(:first-child) {
      text-align: right;
    }
  }
  tfoot {
    tr:first-child {
      background: #B4B4AE;
      border-bottom: 1px solid white !important;
      td:first-child {
        text-align: left
      }
    }
    tr:last-child {
      background: #EDEAE1;
      font-size: 1.05rem;
      color: #181A19;
      td:first-child {
        background: white;
      }
      td:nth-child(2) {
        text-align: left;
      }
    }
  }
}

.condensed-pricing-summary {
  position: relative;
  height: 815px;
}

.summary-info {
  position: absolute;
  width: 100%;
  bottom: 0px;
  left: 0px;
  &__label {
    border-right: 1px solid #D5D3CE;
    height: 30px;
    font-size: 1.2em;
    font-weight: normal;
  }
}

.pricing-desc {
  border-top: 1px solid #D5D3CE;
  font-weight: lighter;
  div.column {
    font-size: 0.75em;
    padding-top: 0px !important;
    padding-bottom: 0px !important;
  }
  .column > div {
    padding: 5px 10px;
  }
  &:not(:first-child) {
    margin-top: 3px !important;
  }
  .notes-content {
    min-height: 26px;
  }
}
</style>